import { useState, type FormEvent, useEffect } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { Link, useNavigate } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import { useCustomers, useCustomerAddresses, useCreateCustomer, useCreateCustomerAddress } from '@/data/customers';
import { useDrivers } from '@/data/members';
import { useCreateLoad, useAddStop, useAddItem, useAssignLoad } from '@/data/mutations';
import { useCreatePosting } from '@/data/load-postings';
import { Button } from '@/components/ui/button';
import { Field, Input, Textarea } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/auth/api';
import { cn } from '@/lib/cn';
import type { Customer } from '@breaker/shared-types';

const VEHICLES: Array<{ value: string; label: string }> = [
  { value: 'cycle', label: 'Cycle' },
  { value: 'cargo_bike', label: 'Cargo bike' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'car', label: 'Car' },
  { value: 'small_van', label: 'Small van' },
  { value: 'midi_van', label: 'Midi van' },
  { value: 'swb', label: 'SWB van (≤2.4m)' },
  { value: 'mwb', label: 'MWB van (≤3m)' },
  { value: 'lwb', label: 'LWB van (≤4m)' },
  { value: 'xlwb', label: 'XLWB van (4m+)' },
  { value: 'luton', label: 'Luton' },
  { value: 'jumbo_luton', label: 'Jumbo Luton' },
  { value: 'rigid_7_5t', label: '7.5T' },
  { value: 'rigid_12t', label: '12T' },
  { value: 'rigid_18t', label: '18T' },
  { value: 'rigid_26t', label: '26T' },
  { value: 'artic', label: '13.6m Artic' },
  { value: 'skellie_1x20', label: 'Skellie 1×20' },
  { value: 'skellie_2x20', label: 'Skellie 2×20' },
  { value: 'skellie_1x40', label: 'Skellie 1×40' },
  { value: 'skellie_1x44', label: 'Skellie 1×44' },
  { value: 'tractor_4axle', label: '4 Axle Tractor' },
  { value: 'tractor_6axle', label: '6 Axle Tractor' },
  { value: 'other', label: 'Other' },
];

interface StopDraft {
  key: number;
  type: 'pickup' | 'dropoff';
  line1: string; line2: string; city: string; postcode: string;
  lat: number | null; lng: number | null;
  lookup: 'idle' | 'loading' | 'ok' | 'notfound';
  contactName: string; contactPhone: string;
  windowStart: string; windowEnd: string;
  instructions: string;
  customerAddressId: string | null;
  saveToBook: boolean;
}
interface ItemDraft {
  key: number; description: string; quantity: string; weightKg: string; stopKey: number;
}

let nextKey = 1;
function blankStop(type: 'pickup' | 'dropoff'): StopDraft {
  return {
    key: nextKey++, type, line1: '', line2: '', city: '', postcode: '',
    lat: null, lng: null, lookup: 'idle', contactName: '', contactPhone: '',
    windowStart: '', windowEnd: '', instructions: '', customerAddressId: null,
    saveToBook: true,
  };
}

function pence(v: string): number {
  const n = Number.parseFloat(v);
  return Number.isNaN(n) || n < 0 ? 0 : Math.round(n * 100);
}
function money(p: number): string {
  return `£${(p / 100).toFixed(2)}`;
}
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

async function lookupPostcode(pc: string): Promise<{ lat: number; lng: number; city: string } | null> {
  try {
    const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc.trim())}`);
    if (!res.ok) return null;
    const json = (await res.json()) as {
      result?: { latitude: number; longitude: number; admin_district: string | null };
    };
    if (!json.result) return null;
    return { lat: json.result.latitude, lng: json.result.longitude, city: json.result.admin_district ?? '' };
  } catch {
    return null;
  }
}

type DispatchMode = 'assign' | 'board' | 'hold';

export function CreateLoadPage() {
  const { fleet } = useActiveFleet();
  const fleetId = fleet?.id ?? '';
  const navigate = useNavigate();
  const toast = useToast();

  // 1. For who?
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const customersQuery = useCustomers(fleet?.id ?? null, { search: customerSearch });
  const addressesQuery = useCustomerAddresses(fleet?.id ?? null, customerId);
  const createCustomer = useCreateCustomer(fleet?.id ?? null);
  const createAddress = useCreateCustomerAddress(fleet?.id ?? null);

  // 2. Where we going?
  const [stops, setStops] = useState<StopDraft[]>([blankStop('pickup'), blankStop('dropoff')]);

  // 3. What we carrying?
  const [items, setItems] = useState<ItemDraft[]>([]);

  // 4. What vehicle?
  const [vehicleType, setVehicleType] = useState('');
  const [tailLift, setTailLift] = useState(false);

  // 5. How much?
  const [service, setService] = useState('');
  const [extras, setExtras] = useState('');
  // VAT default follows the fleet's registration flag (Settings). Effect
  // keyed on fleet id: sets the default once the fleet loads, without
  // stomping a manual toggle mid-edit (only fires when the fleet changes).
  const [addVat, setAddVat] = useState(true);
  useEffect(() => {
    if (fleet) setAddVat(fleet.vatRegistered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleet?.id]);

  // 6. Post to where?
  const [mode, setMode] = useState<DispatchMode>('board');
  const [driverUserId, setDriverUserId] = useState('');
  const [rate, setRate] = useState('');
  const [rateVisible, setRateVisible] = useState(true);
  const driversQuery = useDrivers(fleet?.id ?? null);

  const [notesDriver, setNotesDriver] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Unit D: Enter-key / double-click re-submits are dropped while the
  // multi-step create sequence is in flight (replaces the old busy state).
  const submitLock = useActionLock();

  const createLoad = useCreateLoad({ fleetId });
  const addStop = useAddStop({ fleetId });
  const addItem = useAddItem({ fleetId });
  const assignLoad = useAssignLoad({ fleetId });
  const createPosting = useCreatePosting(fleet?.id ?? null);

  const netPence = pence(service) + pence(extras);
  const vatPence = addVat ? Math.round(netPence * 0.2) : 0;
  const totalPence = netPence + vatPence;
  const ratePence = pence(rate);
  const marginPence = netPence - ratePence;

  const drivers = driversQuery.data ?? [];
  const drops = stops.filter((s) => s.type === 'dropoff');

  function patchStop(key: number, patch: Partial<StopDraft>) {
    setStops((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  }

  async function doLookup(stop: StopDraft) {
    if (!stop.postcode.trim()) return;
    patchStop(stop.key, { lookup: 'loading' });
    const geo = await lookupPostcode(stop.postcode);
    if (geo) {
      patchStop(stop.key, {
        lat: geo.lat, lng: geo.lng, lookup: 'ok',
        ...(stop.city.trim() ? {} : { city: geo.city }),
      });
    } else {
      patchStop(stop.key, { lat: null, lng: null, lookup: 'notfound' });
    }
  }

  function applyAddress(stop: StopDraft, a: {
    id: string; line1: string; line2: string | null; city: string | null;
    postcode: string | null; lat: string | null; lng: string | null;
  }) {
    patchStop(stop.key, {
      customerAddressId: a.id,
      line1: a.line1, line2: a.line2 ?? '', city: a.city ?? '', postcode: a.postcode ?? '',
      lat: a.lat !== null ? Number(a.lat) : null,
      lng: a.lng !== null ? Number(a.lng) : null,
      lookup: a.lat !== null ? 'ok' : 'idle',
    });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!fleet) return;

    const collection = stops[0];
    if (!collection || !collection.line1.trim()) {
      setError('The collection address needs at least line 1.'); return;
    }
    const missingDrop = drops.find((s) => !s.line1.trim());
    if (drops.length === 0) { setError('Add at least one drop.'); return; }
    if (missingDrop) { setError('Every drop needs at least address line 1.'); return; }
    if (mode === 'assign' && !driverUserId) { setError('Choose a driver to assign.'); return; }

    // S79: validate ALL datetimes before anything is created — a bad time
    // must never strand a half-made load (the stopless-draft bug).
    for (const [i, s] of stops.entries()) {
      const label = i === 0 ? 'Collection' : `Drop ${i}`;
      if (s.windowStart && !localToIso(s.windowStart)) {
        setError(`${label}: the time looks incomplete — pick both a date and a time.`); return;
      }
      if (s.windowEnd && !localToIso(s.windowEnd)) {
        setError(`${label}: the "window to" time looks incomplete — pick both a date and a time.`); return;
      }
      if (s.windowStart && s.windowEnd) {
        const a = localToIso(s.windowStart); const b = localToIso(s.windowEnd);
        if (a && b && b <= a) {
          setError(`${label}: the window ends before it starts.`); return;
        }
      }
    }

    // C5: assigning or posting puts the load on the assignment path — the
    // readiness gate needs a "window from" on every stop, so catch it here
    // before anything is created rather than stranding a stuck draft.
    if (mode !== 'hold') {
      for (const [i, s] of stops.entries()) {
        const label = i === 0 ? 'Collection' : `Drop ${i}`;
        if (!s.windowStart) {
          setError(`${label}: add a "window from" time — assignment needs a window on every stop.`); return;
        }
      }
    }

    submitLock.run(() => submitSequence());
  }

  async function submitSequence() {
    // Validated non-empty in handleSubmit before the lock accepted the run.
    const collection = stops[0]!;
    try {
      const { load } = await createLoad.mutateAsync({
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(customerId ? { customerId } : {}),
        ...(localToIso(collection.windowStart) ? { scheduledStartAt: localToIso(collection.windowStart) } : {}),
        ...(vehicleType ? { vehicleType } : {}),
        tailLift,
        ...(notesDriver.trim() ? { notesDriver: notesDriver.trim() } : {}),
        serviceChargePence: pence(service),
        extrasPence: pence(extras),
        vatPence,
        totalPence,
      });

      const stopIds = new Map<number, string>();
      for (const s of stops) {
        const { stop } = await addStop.mutateAsync({
          loadId: load.id,
          body: {
            type: s.type,
            line1: s.line1.trim(),
            line2: s.line2.trim() || null,
            city: s.city.trim() || null,
            postcode: s.postcode.trim() || null,
            ...(s.lat !== null && s.lng !== null ? { lat: s.lat, lng: s.lng } : {}),
            ...(s.customerAddressId ? { customerAddressId: s.customerAddressId } : {}),
            contactName: s.contactName.trim() || null,
            contactPhoneE164: s.contactPhone.trim() || null,
            windowStartAt: localToIso(s.windowStart),
            windowEndAt: localToIso(s.windowEnd),
            instructions: s.instructions.trim() || null,
          },
        });
        stopIds.set(s.key, stop.id);
      }

      for (const it of items) {
        if (!it.description.trim()) continue;
        const stopId = stopIds.get(it.stopKey);
        if (!stopId) continue;
        const qty = Math.max(1, Number.parseInt(it.quantity, 10) || 1);
        const w = Number.parseFloat(it.weightKg);
        await addItem.mutateAsync({
          loadId: load.id,
          body: {
            loadStopId: stopId,
            description: it.description.trim(),
            quantity: qty,
            ...(Number.isFinite(w) && w > 0 ? { weightKg: w } : {}),
          },
        });
      }

      if (customerId) {
        for (const s of stops) {
          if (!s.saveToBook || s.customerAddressId || !s.line1.trim()) continue;
          try {
            await createAddress.mutateAsync({
              customerId,
              body: {
                label: s.line1.trim().slice(0, 40),
                line1: s.line1.trim(),
                line2: s.line2.trim() || null,
                city: s.city.trim() || null,
                postcode: s.postcode.trim() || null,
                ...(s.lat !== null && s.lng !== null ? { lat: s.lat, lng: s.lng } : {}),
              },
            });
          } catch {
            // Non-fatal: the load is what matters; the address can be saved later.
          }
        }
      }

      if (mode === 'assign') {
        await assignLoad.mutateAsync({ loadId: load.id, driverUserId });
        toast.success(`${load.reference} created and assigned.`);
      } else if (mode === 'board') {
        await createPosting.mutateAsync({
          loadId: load.id,
          audience: 'internal',
          assignmentMode: 'claimable',
          ...(ratePence > 0 ? { rateAmount: ratePence, rateVisible } : {}),
        } as Parameters<typeof createPosting.mutateAsync>[0]);
        toast.success(`${load.reference} created and posted to the board.`);
      } else {
        toast.success(`${load.reference} created (held as draft).`);
      }
      await navigate({ to: '/loads/$loadId', params: { loadId: load.id } });
    } catch (err) {
      setError(err instanceof ApiError
        ? `${err.message} — anything already created is on the load's detail page.`
        : 'Something failed mid-way. Check the loads list; partially created loads keep what saved.');
    }
  }

  return (
    <div className="px-8 py-10 max-w-3xl">
      <nav className="text-xs text-breaker-mute mb-4">
        <Link to="/" className="hover:text-breaker-ink">Loads</Link>
        <span className="mx-1.5">/</span>
        <span className="text-breaker-ink-2">New</span>
      </nav>
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">New load</h1>
        <p className="text-sm text-breaker-mute mt-1">
          Six questions, top to bottom. It ends dispatched, not drafted.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 1 · For who? */}
        <Section n={1} title="For who?">
          <CustomerPicker
            customers={customersQuery.data ?? []}
            loading={customersQuery.isLoading}
            search={customerSearch}
            selectedId={customerId}
            onSearchChange={setCustomerSearch}
            onSelect={(id) => {
              setCustomerId(id);
              const found = customersQuery.data?.find((c) => c.id === id);
              if (found) setCustomerSearch(found.name);
            }}
            onClear={() => { setCustomerId(null); setCustomerSearch(''); }}
            onCreate={(name) =>
              createCustomer
                .mutateAsync({ name })
                .then(({ customer }) => {
                  setCustomerId(customer.id);
                  setCustomerSearch(customer.name);
                })
                .catch(() => toast.error('Could not create customer.'))
            }
          />
        </Section>

        {/* 2 · Where we going? */}
        <Section n={2} title="Where we going?">
          <div className="space-y-4">
            {stops.map((s, i) => (
              <StopCard
                key={s.key}
                stop={s}
                index={i}
                canRemove={i > 0 && stops.length > 2}
                addresses={customerId ? (addressesQuery.data ?? []) : []}
                onApplyAddress={(a) => applyAddress(s, a as never)}
                onPatch={(patch) => patchStop(s.key, patch)}
                onLookup={() => void doLookup(s)}
                canSave={Boolean(customerId) && !s.customerAddressId}
                onRemove={() => {
                  setStops((prev) => prev.filter((x) => x.key !== s.key));
                  setItems((prev) => prev.filter((x) => x.stopKey !== s.key));
                }}
              />
            ))}
            <Button type="button" variant="secondary" size="sm"
              onClick={() => setStops((prev) => [...prev, blankStop('dropoff')])}>
              + Add drop
            </Button>
          </div>
        </Section>

        {/* 3 · What we carrying? */}
        <Section n={3} title="What are we carrying?" hint="Optional — goods attach to the drop where they're handled.">
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.key} className="flex gap-2 items-start">
                <Input placeholder="Description — e.g. 2 pallets, boxed parts" value={it.description}
                  onChange={(e) => setItems((p) => p.map((x) => x.key === it.key ? { ...x, description: e.target.value } : x))}
                  className="flex-1" />
                <Input placeholder="Qty" inputMode="numeric" value={it.quantity}
                  onChange={(e) => setItems((p) => p.map((x) => x.key === it.key ? { ...x, quantity: e.target.value } : x))}
                  className="w-16" />
                <Input placeholder="kg" inputMode="decimal" value={it.weightKg}
                  onChange={(e) => setItems((p) => p.map((x) => x.key === it.key ? { ...x, weightKg: e.target.value } : x))}
                  className="w-20" />
                {drops.length > 1 ? (
                  <select value={it.stopKey}
                    onChange={(e) => setItems((p) => p.map((x) => x.key === it.key ? { ...x, stopKey: Number(e.target.value) } : x))}
                    className="h-10 rounded-md border border-breaker-line-2 bg-white px-2 text-sm">
                    {drops.map((d, di) => (
                      <option key={d.key} value={d.key}>Drop {di + 1}</option>
                    ))}
                  </select>
                ) : null}
                <Button type="button" variant="ghost" size="sm"
                  onClick={() => setItems((p) => p.filter((x) => x.key !== it.key))}>
                  {'×'}
                </Button>
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm"
              onClick={() => {
                const lastDrop = drops[drops.length - 1];
                if (!lastDrop) return;
                setItems((p) => [...p, { key: nextKey++, description: '', quantity: '1', weightKg: '', stopKey: lastDrop.key }]);
              }}>
              + Add item
            </Button>
          </div>
        </Section>

        {/* 4 · What vehicle? */}
        <Section n={4} title="What vehicle?">
          <div className="flex items-end gap-4">
            <Field label="Vehicle type" htmlFor="vehicleType" hint="Optional — the class this load needs.">
              <select id="vehicleType" value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}
                className="block w-56 rounded-md bg-white px-3 h-10 text-sm text-breaker-ink border border-breaker-line-2">
                <option value="">Any</option>
                {VEHICLES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-breaker-ink-2 h-10">
              <input type="checkbox" checked={tailLift} onChange={(e) => setTailLift(e.target.checked)}
                className="h-4 w-4 rounded border-breaker-line-2" />
              Tail lift required
            </label>
          </div>
        </Section>

        {/* 5 · How much? */}
        <Section n={5} title="How much?">
          <div className="grid grid-cols-3 gap-3 items-end">
            <Field label="Service charge (GBP)" htmlFor="svc">
              <Input id="svc" type="number" min="0" step="0.01" value={service}
                onChange={(e) => setService(e.target.value)} placeholder="0.00" />
            </Field>
            <Field label="Extras (GBP)" htmlFor="ext">
              <Input id="ext" type="number" min="0" step="0.01" value={extras}
                onChange={(e) => setExtras(e.target.value)} placeholder="0.00" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-breaker-ink-2 h-10">
              <input type="checkbox" checked={addVat} onChange={(e) => setAddVat(e.target.checked)}
                className="h-4 w-4 rounded border-breaker-line-2" />
              + VAT 20% ({money(vatPence)})
            </label>
          </div>
          <div className="mt-3 flex justify-between border-t border-breaker-line pt-2 text-sm font-medium">
            <span className="text-breaker-ink">Total to customer</span>
            <span className="text-breaker-ink">{money(totalPence)}</span>
          </div>
        </Section>

        {/* 6 · Post to where? */}
        <Section n={6} title="Post to where?">
          <div className="flex gap-1 mb-4">
            {([
              ['board', 'Post to board'],
              ['assign', 'Assign to driver'],
              ['hold', 'Hold as draft'],
            ] as Array<[DispatchMode, string]>).map(([m, label]) => (
              <button key={m} type="button" onClick={() => setMode(m)}
                className={cn('rounded-md px-4 py-2 text-sm transition-colors',
                  mode === m ? 'bg-breaker-ink text-white' : 'bg-breaker-ground-2 text-breaker-ink-2 hover:text-breaker-ink')}>
                {label}
              </button>
            ))}
          </div>

          {mode === 'assign' ? (
            <Field label="Driver" htmlFor="driver">
              <select id="driver" value={driverUserId} onChange={(e) => setDriverUserId(e.target.value)}
                className="block w-64 rounded-md bg-white px-3 h-10 text-sm text-breaker-ink border border-breaker-line-2">
                <option value="">Choose driver...</option>
                {drivers.map((d) => <option key={d.userId} value={d.userId}>{d.user.displayName}</option>)}
              </select>
            </Field>
          ) : null}

          {mode === 'board' ? (
            <div className="flex items-end gap-4">
              <Field label="Driver rate (GBP, optional)" htmlFor="rate">
                <Input id="rate" type="number" min="0" step="0.01" value={rate}
                  onChange={(e) => setRate(e.target.value)} placeholder="e.g. 45.00" className="w-40" />
              </Field>
              {rate.trim() ? (
                <>
                  <label className="flex items-center gap-2 text-sm text-breaker-ink-2 h-10">
                    <input type="checkbox" checked={rateVisible} onChange={(e) => setRateVisible(e.target.checked)}
                      className="h-4 w-4 rounded border-breaker-line-2" />
                    Visible to drivers
                  </label>
                  <span className={cn('text-sm h-10 flex items-center',
                    marginPence >= 0 ? 'text-breaker-ink-2' : 'text-breaker-red')}>
                    Margin: {money(marginPence)} ex VAT
                  </span>
                </>
              ) : null}
            </div>
          ) : null}

          {mode === 'hold' ? (
            <p className="text-sm text-breaker-mute">
              Saves as a draft — nothing goes to drivers until you assign or post it.
            </p>
          ) : null}
        </Section>

        <details className="rounded-md border border-breaker-line-2 p-4">
          <summary className="text-sm font-medium text-breaker-ink cursor-pointer">More (reference, driver notes)</summary>
          <div className="mt-3 space-y-3">
            <Field label="Reference" htmlFor="reference" hint="Leave blank to auto-generate (BKR-YYYY-NNNNN).">
              <Input id="reference" value={reference} onChange={(e) => setReference(e.target.value)}
                maxLength={40} className="font-mono" placeholder="Optional — e.g. PO-90412" />
            </Field>
            <Field label="Notes for the driver" htmlFor="notesDriver">
              <Textarea id="notesDriver" rows={2} value={notesDriver} onChange={(e) => setNotesDriver(e.target.value)} />
            </Field>
          </div>
        </details>

        {error ? (
          <div className="rounded-md bg-breaker-red/10 border border-breaker-red/30 px-3 py-2 text-sm text-breaker-red-deep">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Link to="/"><Button variant="secondary" type="button">Cancel</Button></Link>
          <Button type="submit" loading={submitLock.locked}>
            {mode === 'assign' ? 'Create & assign' : mode === 'board' ? 'Create & post' : 'Create load'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ n, title, hint, children }: {
  n: number; title: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <fieldset className="rounded-md border border-breaker-line-2 p-4">
      <legend className="px-1 text-sm font-medium text-breaker-ink">
        <span className="text-breaker-mute mr-1.5">{n}</span>{title}
      </legend>
      {hint ? <p className="text-xs text-breaker-mute mb-3">{hint}</p> : null}
      {children}
    </fieldset>
  );
}

function StopCard({ stop, index, canRemove, addresses, onApplyAddress, onPatch, onLookup, onRemove, canSave }: {
  stop: StopDraft; index: number; canRemove: boolean;
  addresses: Array<{ id: string; label?: string | null; line1: string; line2: string | null; city: string | null; postcode: string | null; lat: string | null; lng: string | null }>;
  onApplyAddress: (a: unknown) => void;
  onPatch: (p: Partial<StopDraft>) => void;
  onLookup: () => void;
  onRemove: () => void;
  canSave: boolean;
}) {
  const isCollection = index === 0;
  return (
    <div className="rounded-md border border-breaker-line p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xs uppercase tracking-wider font-medium text-breaker-ink">
            {isCollection ? 'Collection' : `Drop ${index}`}
          </span>
          {!isCollection ? (
            <label className="flex items-center gap-1.5 text-2xs text-breaker-mute">
              <input type="checkbox" checked={stop.type === 'pickup'}
                onChange={(e) => onPatch({ type: e.target.checked ? 'pickup' : 'dropoff' })}
                className="h-3 w-3 rounded border-breaker-line-2" />
              extra collection
            </label>
          ) : null}
        </div>
        {canRemove ? (
          <button type="button" onClick={onRemove}
            className="text-xs text-breaker-mute hover:text-breaker-red">Remove</button>
        ) : null}
      </div>

      {addresses.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {addresses.map((a) => (
            <button key={a.id} type="button" onClick={() => onApplyAddress(a)}
              className={cn('rounded-full border px-2.5 py-1 text-2xs transition-colors',
                stop.customerAddressId === a.id
                  ? 'border-breaker-ink bg-breaker-ink text-white'
                  : 'border-breaker-line-2 text-breaker-ink-2 hover:border-breaker-ink')}>
              {a.label ?? a.line1}{a.postcode ? ` · ${a.postcode}` : ''}
            </button>
          ))}
        </div>
      ) : null}

      <Input value={stop.line1} placeholder="Address line 1"
        onChange={(e) => onPatch({ line1: e.target.value, customerAddressId: null })} />
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
        <Input value={stop.city} placeholder="City / town"
          onChange={(e) => onPatch({ city: e.target.value })} />
        <Input value={stop.postcode} placeholder="Postcode"
          onChange={(e) => onPatch({ postcode: e.target.value, lookup: 'idle', lat: null, lng: null, customerAddressId: null })}
          onBlur={onLookup} />
        <span className={cn('flex items-center text-2xs w-16',
          stop.lookup === 'ok' ? 'text-breaker-green-deep' :
          stop.lookup === 'notfound' ? 'text-breaker-red' :
          stop.lookup === 'loading' ? 'text-breaker-mute' : 'text-transparent')}>
          {stop.lookup === 'ok' ? '✓ located' :
           stop.lookup === 'notfound' ? 'not found' :
           stop.lookup === 'loading' ? '…' : '.'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input value={stop.contactName} placeholder="Contact name (optional)"
          onChange={(e) => onPatch({ contactName: e.target.value })} />
        <Input value={stop.contactPhone} placeholder="Contact phone +44..."
          onChange={(e) => onPatch({ contactPhone: e.target.value })} />
      </div>
      {/* S79: datetime-local needs real width — Firefox clips the time
          segments off the right edge when the input is squeezed (the
          stopless-draft bug's front end). C6: fluid inside a responsive
          grid — full-width per column instead of a fixed min-width, so
          narrow viewports stack instead of overflowing. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label={isCollection ? 'Collection time' : 'Window from'} htmlFor={`ws-${stop.key}`}
          hint="Required to assign">
          <Input id={`ws-${stop.key}`} type="datetime-local" value={stop.windowStart}
            className="min-w-0 w-full"
            onChange={(e) => onPatch({ windowStart: e.target.value })} />
        </Field>
        <Field label="Window to" htmlFor={`we-${stop.key}`} hint="Optional">
          <Input id={`we-${stop.key}`} type="datetime-local" value={stop.windowEnd}
            className="min-w-0 w-full"
            onChange={(e) => onPatch({ windowEnd: e.target.value })} />
        </Field>
      </div>
      <Input value={stop.instructions} placeholder="Instructions — access, parking, who to ask for (optional)"
        onChange={(e) => onPatch({ instructions: e.target.value })} />
      {canSave && stop.line1.trim() ? (
        <label className="flex items-center gap-2 text-2xs text-breaker-mute">
          <input type="checkbox" checked={stop.saveToBook}
            onChange={(e) => onPatch({ saveToBook: e.target.checked })}
            className="h-3 w-3 rounded border-breaker-line-2" />
          Save to customer's address book
        </label>
      ) : null}
    </div>
  );
}

function CustomerPicker({ customers, loading, search, selectedId, onSearchChange, onSelect, onClear, onCreate }: {
  customers: Customer[]; loading: boolean; search: string; selectedId: string | null;
  onSearchChange(s: string): void; onSelect(id: string): void; onClear(): void;
  onCreate(name: string): Promise<unknown>;
}) {
  const [focused, setFocused] = useState(false);
  const showResults = focused && customers.length > 0 && !selectedId;
  return (
    <Field label="Customer" htmlFor="customerSearch" hint="Type to search. Their saved addresses appear on the stops below.">
      <div className="relative max-w-md">
        <Input id="customerSearch" value={search} placeholder="Type to search customers" autoComplete="off"
          onChange={(e) => { onSearchChange(e.target.value); if (selectedId) onClear(); }}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)} />
        {selectedId ? (
          <button type="button" onClick={onClear} aria-label="Clear customer"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-breaker-mute hover:text-breaker-ink px-1">
            {'×'}
          </button>
        ) : null}
        {showResults ? (
          <ul className="absolute z-10 mt-1 w-full rounded-md bg-white border border-breaker-line-2 shadow-sm max-h-56 overflow-y-auto py-1">
            {customers.map((c) => (
              <li key={c.id}>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); onSelect(c.id); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-breaker-ground-2">
                  <span className="block text-breaker-ink">{c.name}</span>
                  {c.accountRef ? <span className="block text-2xs text-breaker-mute">{c.accountRef}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {focused && search.trim() && !loading && customers.length === 0 && !selectedId ? (
          <div className="absolute z-10 mt-1 w-full rounded-md bg-white border border-breaker-line-2 shadow-sm py-1">
            {/* Unit D rule 4: shared Button + action so the create can't
                double-fire; mousedown stays prevented so the input keeps
                focus until the click lands. */}
            <Button type="button" variant="ghost"
              onMouseDown={(e) => e.preventDefault()}
              action={() => onCreate(search.trim())}
              className="w-full h-auto justify-start whitespace-normal rounded-none px-3 py-2 text-left text-sm font-normal text-breaker-ink hover:bg-breaker-ground-2">
              + Add "{search.trim()}" as a new customer
            </Button>
          </div>
        ) : null}
      </div>
    </Field>
  );
}
